// handling selected global vars to be shared between pages via localstorage

import storage from "./localstorage";
import {
  copyProps,
  filterObject,
  getAnyProp,
  getProp,
  isObject,
  jsonParse,
} from "./util";
import { getPropertyStateKeys } from "./globals";
import { getGlobal, setGlobal, setGlobalValue, getGlobalValue } from "./global-util";
import getLogger from "./debug-logger";
import { filterRequestedProducts } from "./products";

const log = getLogger("stored-data", 3);

//------------------------------------------------------
// Localstorage data utility functions
//------------------------------------------------------

const dataTTL = 1000 * 60 * 60 * 3;

const storageKey = "avm_property_data";

//------------------------------------------------------
// store/retrieve last property address for reset purpose
//------------------------------------------------------
export const storeInitAddress = (addrLine) => {
  storage.set("init_address", addrLine);
};

export const popInitAddress = () => storage.pop("init_address");

//------------------------------------------------------
// check if last property data in localstorage exists
//------------------------------------------------------
export const storedPropertyDataExists = () => !!getStoredData();

//------------------------------------------------------
// write property data into localstorage
//------------------------------------------------------
const setStoredData = (obj) => {
  if (!isObject(obj)) {
    log(`object expected. Actual value:`, obj); //---------------- log
    return;
  }
  if (!getProp(obj, "DATA_EXPIRES")) {
    obj.DATA_EXPIRES = Date.now() + dataTTL;
  }
  storage.set(storageKey, obj);
};

//------------------------------------------------------
// delete property data from localstorage
//------------------------------------------------------
export const removeStoredData = () => {
  log(`removing property info from locastorage`); //--------- log
  storage.remove(storageKey);
};

//------------------------------------------------------
// read property data from localstorage
//------------------------------------------------------
export const getStoredData = () => {
  const obj = storage.get(storageKey);
  if (!isObject(obj)) {
    log(`no stored property data`); //--------- log
    return null;
  }
  log(`stored property address:`, obj.ADDRESS_LINE); //-------- log
  if (!getProp(obj, "FEATURES_CURRENT")) {
    log(`stored property data is corrupted`); //--------- log
    return null;
  }
  const expTime = Number(getProp(obj, "DATA_EXPIRES"));
  if (!expTime || expTime < Date.now()) {
    log(`stored property data has expired`); //--------- log
    removeStoredData();
    return null;
  }
  return obj;
};

//------------------------------------------------------
// collect current property data and put it in localstopage
//------------------------------------------------------
export const storePropertyData = () => {
  const obj = getGlobal();
  const keys = [...getPropertyStateKeys()].filter((k) => getProp(obj, k, null) !== null); // keys of all property-related globals
  const propertyInfo = copyProps(keys, obj);
  log(`saving property info in localstorage. Keys:`, Object.keys(propertyInfo).sort()); //--------- log
  setStoredData(propertyInfo);
};

//------------------------------------------------------
// update stored property data and save it in localstopage
//------------------------------------------------------
export const updateStoredData = (obj) => {
  if (!isObject(obj)) {
    log(`object expected, received value is`, obj); //-------------- log
    return;
  }
  log(`preparing updates`, obj); //---------------- log
  const info = getStoredData();
  // data from initial load must already exist
  if (!isObject(info)) {
    return;
  }
  const updated = filterObject({ ...info, ...obj });
  setStoredData(updated);
};

//------------------------------------------------------
// update current data snapshot by single data point
//------------------------------------------------------
export const setStoredValue = (key, value) => {
  updateStoredData({ [key]: value });
};

//------------------------------------------------------
// update stored data in sync with globals
//------------------------------------------------------
export const setAndStoreGlobals = (obj) => {
  if (isObject(obj)) {
    updateStoredData(obj);
    setGlobal(obj);
  } else {
    log("setAndStoreGlobals: object expected. Found:", obj);
  }
};

//------------------------------------------------------
// update current data snapshot and global object by single data point
//------------------------------------------------------
export const setStoredGlobalValue = (key, val) => {
  setStoredValue(key, val);
  setGlobalValue(key, val);
};

//------------------------------------------------------
// keep track on requested products in localstorage
//------------------------------------------------------
export const storeRequestedProducts = (prodList) => {
  const newProducts = Array.isArray(prodList) ? prodList : [];
  let reqProducts = getGlobalValue("REQUESTED_PRODUCTS") || [];
  reqProducts = Array.from(new Set([...reqProducts, ...newProducts]));
  // remove untracked products from the list
  reqProducts = filterRequestedProducts(reqProducts);
  log(`storing requested products:`, reqProducts); //--------- log
  setStoredGlobalValue("REQUESTED_PRODUCTS", reqProducts);  
}

//------------------------------------------------------
// restore last saved property data and update global object
//------------------------------------------------------
export const restoreStoredData = () => {
  const storedData = getStoredData();
  if (!storedData) {
    log(`no stored property data found`); //------------ log
    return false;
  }
  const updates = { ...storedData, ...jsonParse(storedData.FEATURES_CURRENT) };

  log(`restoring property info from localstorage`, updates); //--------- log
  updates.PROPERTY_SOURCE = "cache";
  setGlobal(updates);

  return true;
};

//------------------------------------------------------
// check if valid property data exists in localstorage
//------------------------------------------------------
export const storedDataExists = () => !!getStoredData();

//------------------------------------------------------
// invalidate PDF report and Property Snapshot PDF URLs - global and stored
//------------------------------------------------------
export const dropReport = () => {
  const upd = { REPORT_URL: null, REPORT_COMP_URL: null, SNAPSHOT_URL: null };
  setGlobal(upd);
  updateStoredData(upd);
};

//--------------------------------------------------------------
// committing changes in property stats
//--------------------------------------------------------------
export const saveUpdatedStats = () => {
  const glob = getGlobal();
  const statsJson = getProp(glob, "STATS_CURRENT");
  const prevStatsJson = getAnyProp(glob, "STATS_INITIAL", "STATS_UPDATED");
  if (statsJson && statsJson !== prevStatsJson) {
    log(`updating property stats`, jsonParse(statsJson)); //------------- log
    setStoredGlobalValue("STATS_UPDATED", statsJson);
  }
};
